/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Directive, EventEmitter, HostBinding, HostListener, Input, Output } from '@angular/core';
import { LogService } from '../services';

@Directive({ selector: '[appCopyClipboard]' })
export class CopyToClipboardDirective {
    /**
     * Text to be copied
     * @param text
     */
    @Input() set textToCopy(text: any) {
        this._textToCopy = text;
        if (this._storedElement) {
            this._storedElement.textContent = this.originalButtonText;
        }
    }

    /**
     * Boolean to updated text on the button after a successful copy.
     */
    @Input() public updateButtonText: boolean = true;

    /**
     * Emit to the calling component
     */
    @Output() public copied: EventEmitter<string> = new EventEmitter<string>();

    /**
     * Store original button text
     */
    public originalButtonText: string;

    /**
     * Local store of the text to copy
     * @private
     */
    private _textToCopy: string;

    /**
     * Local store of the element being clicked.
     * @private
     */
    private _storedElement: any;

    constructor(private logService: LogService) {
    }

    /**
     * Add a class to the button element.
     */
    @HostBinding('class')
    public elementClass: string = 'copy-button';

    @HostBinding('title')
    public elementTitle: string = 'Copy to clipboard';

    /**
     * Click event listener
     * @param event
     */
    @HostListener('click', ['$event'])
    public onClick(event: MouseEvent): void {
        // Stop any other actions on the element being clicked (e.g. submit form)
        event.preventDefault();
        if (!this._textToCopy) {
            return;
        }

        const cleanText = this._textToCopy.toString().replace(/(<([^>]+)>)/gi, '');

        navigator.clipboard.writeText(cleanText).then(
            (): void => {
                if (this.updateButtonText) {
                    this._storedElement = event.target;
                    this.originalButtonText = this._storedElement.textContent;
                    this._storedElement.textContent = 'Copied';
                    const timerId = setTimeout(() => {
                        this._storedElement.textContent = this.originalButtonText;
                        clearInterval(timerId);
                    }, 1000);
                }
                this.copied.emit(this._textToCopy);
            },
            () => {
                /* clipboard write failed */
                this.logService.error('CopyToClipboardDirective', 'There was an error copying to your clipboard');
                this.copied.emit(this._textToCopy);
            },
        );
    }
}
